<script setup>
import { keyBy, uniqBy } from 'lodash-es';
import { onMounted, onUnmounted, provide, reactive, ref, watch, watchEffect } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { useMap } from '~/common/composables/mapbox/maps';
import { useMapboxFeatureSelection } from '~/common/composables/mapbox/selection';
import useEmitter from '~/common/composables/useEmitter';
import { useCustomViewStore } from '~/common/stores/custom-view.store.js';
import { usePusherStore } from '~/common/stores/pusher.store.js';
import TerraNewForm from '~/forms/components/new-form/terra-new-form.vue';
import { useFormPermissions } from '~/forms/composables/form-permissions.composable';
import { useFormsStore } from '~/forms/store/forms.store';
import TerraTaskForm from '~/tasks/components/molecule/task-form/terra-task-form.vue';
import { useTasksStore } from '~/tasks/store/tasks.store';
import FeaturesSelected from '~/terra/components/barcode-scan-map/features-selected.vue';
import MapTools from '~/terra/components/map-tools.vue';
import TerraSidebarMenu from '~/terra/components/terra-sidebar-menu.vue';
import { useBarcodeScanHelper } from '~/terra/composables/barcode-scan-helper.composable';
import { processViewConfig, toggleLabels, useFeatureSelection } from '~/terra/composables/use-common-helpers';
import { useTerraStore } from '~/terra/store/terra.store';
import { useFeatureCluster } from '~/terra/utils/feature-cluster.composable';
import { useTerraHelperComposable } from '~/terra/utils/helper-composable';
import { useTaskFormCluster } from '~/terra/utils/task-form-cluster.composable';
import { useTasksFormsComposable } from '~/terra/utils/terra-tasks-forms-helper.composable.js';

const { route, $t, router, auth_store } = useCommonImports();
const terra_store = useTerraStore();
const custom_view_store = useCustomViewStore();
const task_store = useTasksStore('terra_task_store');
const forms_store = useFormsStore('terra_form_store');
const emitter = useEmitter();

watchEffect(() => {
  const pusher_store = usePusherStore();
  if (pusher_store.is_initialized) {
    task_store.subscribe_pusher({
      organization: auth_store.current_organization?.uid,
      ...(route.params.asset_id ? { asset: route.params.asset_id } : {}),
    });
  }
}, { flush: 'post' });

const { getFeatureStyle, loadBarcodeScanImages, addBarcodeScanSymbols } = useBarcodeScanHelper();
const { addFeatureClusters } = useFeatureCluster();
const { loadTaskFormIcons, addSymbols, dragLayers } = useTerraHelperComposable();
const { addTaskFormClusters } = useTaskFormCluster();
const { fetchProjectsTasksAndForms } = useTasksFormsComposable();
const { syncAndGetButtonState } = useFormPermissions();
provide('$form_create_permission', syncAndGetButtonState());

const active_tool = ref('');
const open_task_popup = ref(false);
const open_form_popup = ref(false);
const project_details_for_task_form_creation = ref(null);

const { loadSelectionEvents } = useFeatureSelection({ active_tool, layers: [], store: terra_store });
const { loadSelectionEvents: loadTaskFormSelectionEvents } = useMapboxFeatureSelection({
  layer_ids: ['symbol-layer-icon'],
  style_selection: false,
  use_normal_selection: true,
  task_form_selection: true,
}, (e, type) => {
  if (!['update', 'create', 'selectionchange'].includes(type)) {
    const tasksForms = uniqBy(e, f => f.properties.uid).map(item => ({
      geometry: item.geometry || item._geometry,
      id: item.id,
      properties: item.properties,
      type: item.type,
    }));
    if (tasksForms.length === 1 && type === 'click') {
      const task_form = tasksForms[0].properties;
      if (task_form.feature_type === 'task') {
        router.push({
          ...route,
          query: {
            task: btoa(JSON.stringify({
              id: task_form.task_form_uid,
              store_key: 'terra_task_store',
            })),
          },
        });
      }
      else {
        const form_detail = JSON.parse(task_form.data);
        router.push({
          ...route,
          query: {
            form: btoa(JSON.stringify({
              form_uid: task_form.task_form_uid,
              feature_uid: form_detail.target_element?.stage === 'TERRA' && form_detail.target_element?.type === 'feature' ? form_detail.target_element.uid : null,
              store_key: 'terra_form_store',
            })),
          },
        });
      }
      return;
    }
    terra_store.selected_tasks_forms = keyBy(tasksForms, feature => feature.properties.task_form_uid);

    const symbolSourceItems = terra_store.map.getSource('symbol-source')._data;
    symbolSourceItems.features = symbolSourceItems.features.map((f) => {
      f.properties.icon = f.properties.icon.replace('-selected', '');
      if (terra_store.selected_tasks_forms[f.properties.task_form_uid])
        f.properties.icon += '-selected';

      return f;
    });

    terra_store.map.getSource('symbol-source').setData(symbolSourceItems);
  }
});

const sidebar_state = reactive({
  active_menu: 'layers',
  is_table_active: false,
});

// Computed properties for loading projects in the map
const projects_request_status = computed(() => terra_store.projects_request_status);
const progress_text = computed(() => {
  return `${$t('Loading layers')} (${projects_request_status.value.current}/${projects_request_status.value.total})`;
});

function sourcesLayers() {
  return {
    sources: [
      'all_features_source',
      'barcode-scan-symbol-source',
      'symbol-source',
    ],
    layers: [
      // Fill layer for polygons
      {
        id: 'polygon_feature_layer',
        type: 'fill',
        source: 'all_features_source',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': getFeatureStyle.value.default['fill-color'],
          'fill-opacity': 1,
        },
      },
      // Outline layer for polygons
      {
        id: 'polygon_outline_layer',
        type: 'line',
        source: 'all_features_source',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'line-color': getFeatureStyle.value.default['line-color'],
          'line-width': 2.5,
          'line-opacity': 1,
        },
      },
      // Point features
      {
        id: 'point_feature_layer',
        type: 'circle',
        source: 'all_features_source',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-color': getFeatureStyle.value.default['fill-color'] || getFeatureStyle.value.default['line-color'],
          'circle-opacity': 1,
          'circle-stroke-color': getFeatureStyle.value.default['line-color'],
        },
      },
      // Line features
      {
        id: 'linestring_feature_layer',
        type: 'line',
        source: 'all_features_source',
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: getFeatureStyle.value.default,
      },
      {
        id: 'barcode-scan-symbol-layer',
        type: 'symbol',
        source: 'barcode-scan-symbol-source',
        layout: {
          'icon-image': ['coalesce', ['get', 'icon'], 'scan-duplicates'],
          'icon-size': ['coalesce', ['get', 'icon_size'], 1],
          'icon-allow-overlap': true,
        },
      },
      {
        id: 'symbol-layer-icon',
        type: 'symbol',
        source: 'symbol-source',
        layout: {
          'icon-image': ['coalesce', ['get', 'icon'], 'marker'],
          'icon-size': ['coalesce', ['get', 'icon_size'], 1],
          'icon-allow-overlap': true,
        },
      },
    ],
  };
}

const { initMapbox, loadMapBoxPackage, addMapboxToken, initDraw, setSources, setLayers, removeMapboxInstance } = useMap({}, async (event_data, event_name) => {
  if (event_name === 'loaded') {
    loadSelectionEvents({ mapbox_instance: terra_store.map });
    loadTaskFormSelectionEvents({ mapbox_instance: terra_store.map });
    loadBarcodeScanImages(terra_store.map);
    loadTaskFormIcons({ map: terra_store.map });
    const { sources, layers } = sourcesLayers();
    setSources(sources, terra_store.map);
    setLayers(layers, terra_store.map);
    terra_store.draw = await initDraw(terra_store.map);
    dragLayers({
      layer: 'symbol-layer-icon',
      source: 'symbol-source',
      save_location: false,
      layer_type: 'task/form',
      map_instance: terra_store.map,
    });
    await getData();
  }
});

async function initializeMap() {
  try {
    terra_store.is_loading = true;
    await loadMapBoxPackage();
    await addMapboxToken();

    terra_store.map = await initMapbox({
      container_id: 'barcode-scan-map',
      style: 'mapbox://styles/mapbox/streets-v11',
    });
    terra_store.map.boxZoom.disable();

    terra_store.is_loading = false;
  }
  catch (error) {
    logger.error(error);
    terra_store.is_loading = false;
  }
}

async function getData(view_config = null) {
  try {
    terra_store.is_loading = true;

    await terra_store.set_container({
      uid: route.params.id,
      forceUpdate: true,
    });

    const custom_view_data = custom_view_store.views_map[route.query?.view_uid];
    const config = view_config || custom_view_data?.data;
    const view_present = await checkAndUpdateView(config);

    if (!view_present) {
      const firstGroup = Object.values(terra_store.container.groups)[0];
      if (firstGroup) {
        const firstProject = Object.values(firstGroup.projects)[0];
        if (firstProject) {
          await terra_store.toggle_project({
            project: firstProject,
            do_not_fly: false,
          });
        }
      }
    }

    terra_store.set_barcode_scan_duplicates();

    toggleLabels();
    addBarcodeScanSymbols();

    if (!view_present) {
      sidebar_state.active_menu = 'layers';
      sidebar_state.is_table_active = false;
    }

    terra_store.is_loading = false;
  }
  catch (error) {
    logger.error('Error fetching container or toggling project:', error);
    terra_store.is_loading = false;
  }
}

async function checkAndUpdateView(config) {
  if (!config) {
    return false;
  }
  await processViewConfig(config, terra_store);

  if (config.center) {
    terra_store.map.flyTo({
      center: [config.center.lng, config.center.lat],
      zoom: config.zoom_level || 18,
      speed: 4,
    });
  }
  if (config.tasks_cluster)
    terra_store.tasks_cluster = config.tasks_cluster;
  if (config.forms_cluster)
    terra_store.forms_cluster = config.forms_cluster;
  if (config.barcode_scan_map_settings) {
    terra_store.barcode_scan_map_settings = { ...terra_store.barcode_scan_map_settings, ...config.barcode_scan_map_settings };
  }
  if (config.feature_config)
    terra_store.settings = { ...terra_store.settings, ...config.feature_config };
  if (config.table_state)
    terra_store.table_state = config.table_state;
  if (config.sidebar_menu) {
    sidebar_state.active_menu = config.sidebar_menu.active_menu;
    sidebar_state.is_table_active = config.sidebar_menu.is_table_active;
  }
  else {
    sidebar_state.active_menu = 'layers';
  }
  return true;
}
function handleUpdateTableView(e) {
  sidebar_state.is_table_active = e;
  if (!e) {
    terra_store.table_state = {
      search: '',
      filters: [],
      ordered_keys_map: {},
      sort: [],
      should_sort_selected_features: false,
    };
  }
}

async function changeView(view_config = null) {
  try {
    const projects = Object.entries(terra_store.active_projects_map).filter(([, project]) => project.ortho_enabled).map(([project_uid]) => terra_store.active_projects_data_map({ all_projects: true })[project_uid]);
    terra_store.reset_store_except(['draw', 'map', 'container', 'ftg', 'sm_instances_map', 'terra_workflows']);
    terra_store.parse_container_data({ container: terra_store.container, keep_project_map: false }); // to set active projects map and active groups map
    sidebar_state.is_table_active = false;
    await getData(view_config);
    terra_store.set_projects_ortho({ projects });
  }
  catch (error) {
    logger.log('Error Occurred', error);
    terra_store.is_loading = false;
  }
}

// Function to check if projects are loading
async function exporting() {
  while (projects_request_status.value.show && !projects_request_status.value.cancel) {
    await new Promise((resolve) => {
      setTimeout(() => resolve('loading layers'), 2000);
    });
  }
}

async function onContainerChange(e) {
  const url = router.resolve({
    name: 'terra-barcode-scan',
    params: {
      asset_id: route.params.asset_id,
      id: e.uid,
    },
  }).href;
  window.location.href = url;
}
async function getTasksAndForms() {
  try {
    await fetchProjectsTasksAndForms({ tasks: true, forms: true });
    addSymbols({ addTaskFormClusters });
  }
  catch (error) {
    logger.error(error);
  }
}

function openCreationPopup(type) {
  if (type === 'task')
    open_task_popup.value = true;
  else
    open_form_popup.value = true;
}

function closeCreationPopup() {
  open_task_popup.value = false;
  open_form_popup.value = false;
  project_details_for_task_form_creation.value = null;
}

watch(() => terra_store.selected_features, () => {
  terra_store.update_map_features_and_polygon();
});
watch(() => terra_store.should_use_boundary ? terra_store.polygon : terra_store.active_projects, () => {
  getTasksAndForms();
});
watch(() => [task_store?.tasks(), forms_store.forms, terra_store.gallery_view_state.is_active, terra_store.update_symbols_on_the_map_flag], () => {
  addSymbols({ addTaskFormClusters });
});
watch(() => terra_store.tasks_cluster, () => {
  addSymbols({ addTaskFormClusters });
}, { deep: true });
watch(() => terra_store.forms_cluster, () => {
  addSymbols({ addTaskFormClusters });
}, { deep: true });
watch(() => terra_store.features_on_map, () => {
  addBarcodeScanSymbols();
  toggleLabels();
  addFeatureClusters(terra_store, terra_store.features_on_map);
});
watch(() => terra_store.settings.show_counts, () => {
  addFeatureClusters(terra_store, terra_store.features_on_map);
});
watch(() => route.params.asset_id, (val) => {
  if (val)
    router.push({ name: 'maps-list', params: { type: 'maps-list', asset_id: val } });
});

onMounted(async () => {
  await initializeMap();
  emitter.on('form_save', () => {
    addSymbols({ addTaskFormClusters });
  });
});
onUnmounted(() => {
  emitter.off('form_save');
  removeMapboxInstance(terra_store.map);
  terra_store.$reset();
});
</script>

<template>
  <div class="relative -top-0.5">
    <div v-if="projects_request_status.show && !projects_request_status.cancel">
      <HawkExportToast :key="projects_request_status.current" class="z-[102]" :submit="exporting" :progress_text="progress_text" :completed_text="$t('Loaded blocks successfully')" @cancel="terra_store.projects_request_status.cancel = true">
        <template v-if="projects_request_status.current < projects_request_status.total" #title>
          {{ progress_text }}
        </template>
      </HawkExportToast>
    </div>
    <div
      v-if="terra_store.is_loading && !projects_request_status.show"
      class="absolute w-full h-[calc(100vh-65px)] z-[1001] bg-gray-100 opacity-[0.9] flex justify-center items-center"
    >
      <HawkLoader />
    </div>
    <div class="flex w-full">
      <TerraSidebarMenu
        :sidebar_state="sidebar_state"
        @update-active-menu="sidebar_state.active_menu = $event"
        @update-table-active="handleUpdateTableView($event)"
        @toggle-labels="toggleLabels"
        @container-change="onContainerChange"
        @group-or-project-toggled="terra_store.show_save_view = true"
        @save-view="terra_store.show_save_view = true"
        @set-symbols="addSymbols({ addTaskFormClusters })"
        @open-creation-popup="openCreationPopup"
      />
      <div
        class="relative w-full"
        :class="{ 'ml-[80px]': !sidebar_state.is_table_active }"
      >
        <div
          class="mx-5 !h-auto fixed top-16 z-[99] transition-all ease-in-out duration-300"
          :style="`${
            Boolean(sidebar_state.active_menu.length)
              ? 'left:370px; width:calc(100vw - 390px);'
              : 'left:70px; width:calc(100vw - 90px);'
          }`"
        >
          <MapTools
            type="map-view"
            :sidebar_state="sidebar_state"
            :active_tool="active_tool"
            :active_tools="{}"
            :disabled="[]"
            @change-view="changeView"
            @close-save-view="terra_store.show_save_view = false"
            @open-creation-popup="openCreationPopup"
          />
          <TerraTaskForm v-if="open_task_popup" opened_from="Direct" :project_details_for_task_form_creation="project_details_for_task_form_creation" class="absolute right-[370px] mt-10" @close="closeCreationPopup" />
          <TerraNewForm v-if="open_form_popup" opened_from="Direct" :project_details_for_task_form_creation="project_details_for_task_form_creation" class="absolute right-[370px] mt-10" @close="closeCreationPopup" />
        </div>
        <FeaturesSelected
          :sidebar-state="sidebar_state"
          @open-creation-popup="openCreationPopup"
        />
        <div id="barcode-scan-map" class="w-full h-full" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
#barcode-scan-map {
  position: fixed;
  width: 100%;
  top: 65px;
  left: 0;
  bottom: 0;
  right: 0;
  height: 100vh;

  :deep(.mapboxgl-control-container) {
    display: none !important;
  }

  :deep(.boxdraw)  {
    background: rgba(56, 135, 190, 0.1);
    border: 2px solid #3887be;
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
  }
}
</style>
